import { useState } from "react";
import { Link } from "react-router-dom";
import { faqs, testimonials } from "@/mocks/anime";

const toneMap: Record<string, string> = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-700",
  secondary: "bg-secondary-100 text-secondary-700",
};

export function FaqSection() {
  const [open, setOpen] = useState<number>(0);
  return (
    <section className="bg-background-100">
      <div className="container-x py-14 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-primary-700">常见问题</span>
            <h2 className="mt-2 font-heading text-2xl text-foreground-900 md:text-3xl">关于新御宅屋，你关心的问题</h2>
            <p className="mt-2 text-sm leading-6 text-foreground-600">
              第一次来不知道怎么用？这里整理了同好们最常问的问题，几分钟就能上手。
            </p>
            <div className="mt-6 rounded-2xl bg-primary-100/70 p-5">
              <h3 className="flex items-center gap-2 font-bold text-primary-900">
                <i className="ri-question-line"></i>
                还有别的问题？
              </h3>
              <p className="mt-1.5 text-sm leading-6 text-primary-900/80">
                欢迎直接联系我们，或到「求番推荐」里和同好们聊聊，说不定马上就有答案。
              </p>
              <Link
                to="/contact"
                className="mt-4 inline-flex items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors duration-200 hover:bg-primary-600"
              >
                联系我们
              </Link>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {faqs.map((f, i) => {
              const isOpen = open === i;
              return (
                <div key={f.q} className={`card overflow-hidden transition-colors duration-200 ${isOpen ? "border-primary-300" : ""}`}>
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                  >
                    <h3 className="text-sm font-bold text-foreground-900 md:text-base">{f.q}</h3>
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm transition-all duration-300 ${
                        isOpen ? "rotate-180 bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                      }`}
                    >
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </button>
                  <div
                    className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
                  >
                    <div className="overflow-hidden">
                      <p className="px-5 pb-5 text-sm leading-6 text-foreground-600">{f.a}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export function TestimonialsSection() {
  return (
    <section className="container-x py-14 md:py-16">
      <div className="mb-8 max-w-2xl">
        <span className="text-xs font-bold uppercase tracking-widest text-accent-700">同好口碑</span>
        <h2 className="mt-2 font-heading text-2xl text-foreground-900 md:text-3xl">来自同好的真实评价</h2>
        <p className="mt-2 text-sm leading-6 text-foreground-600">
          从一个默默追番的人，到在这里找到组织，听听同好们怎么说。
        </p>
      </div>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        {testimonials.map((t) => (
          <figure key={t.name} className="card flex h-full flex-col p-6">
            <div className="flex gap-1 text-accent-400">
              {Array.from({ length: 5 }).map((_, i) => (
                <i key={i} className="ri-star-fill"></i>
              ))}
            </div>
            <blockquote className="mt-4 flex-1 text-sm leading-6 text-foreground-700">“{t.text}”</blockquote>
            <figcaption className="mt-5 flex items-center gap-3">
              <span className={`flex h-10 w-10 items-center justify-center rounded-full text-base font-bold text-background-50 ${toneMap[t.color]}`}>
                <i className="ri-user-3-line"></i>
              </span>
              <div>
                <div className="text-sm font-bold text-foreground-900">{t.name}</div>
                <div className="text-xs text-foreground-500">{t.role}</div>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

export function CtaBanner() {
  return (
    <section className="container-x pb-14 md:pb-16">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 px-6 py-12 text-center md:px-12 md:py-16">
        <div className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-accent-400/30 blur-2xl"></div>
        <div className="pointer-events-none absolute -bottom-10 -left-6 h-40 w-40 rounded-full bg-background-50/20 blur-2xl"></div>
        <div className="relative z-10">
          <h2 className="font-heading text-2xl text-background-50 md:text-3xl">还不知道下一部看什么？</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-background-50/90 md:text-base">
            来新御宅屋求番推荐，同好帮你把下一部要追的番选出来。错过一部好番，比踩雷更可惜。
          </p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              to="/recommend"
              className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-background-50 px-6 py-3 text-sm font-bold text-primary-700 transition-colors duration-200 hover:bg-background-100"
            >
              去求番推荐
            </Link>
            <Link
              to="/order"
              className="inline-flex items-center justify-center whitespace-nowrap rounded-full border-2 border-background-50/70 px-6 py-3 text-sm font-bold text-background-50 transition-colors duration-200 hover:bg-background-50 hover:text-foreground-800"
            >
              看补番攻略
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}